import React from 'react';
import type { SiteRagReport } from '../../../types/siteRag';
import { resolveLcoeDataProvenance } from '../../../lib/lcoeDisplay';
import { projectDerivedTableReferences } from '../../../lib/report/derivedTableReferences';
import { walkCitationText } from '../../../lib/walkCitationText';
import { ReportSourceNote } from './ReportSourceNote';

/**
 * W0 shared-foundation fix — the default className carried the APP-chrome
 * `text-text-muted` token, leaking app colours into report section bodies
 * (LcoeBreakdownSection + the inline feasibility LCOE block). The muted ink
 * now routes through `ReportSourceNote`, so the footnote follows the active
 * report preset and the shared source-attribution typography on every surface.
 *
 * Full bibliographic strings live in the report-wide appendix. This shared
 * note keeps only compact report-global reference markers and the report-date
 * qualifier beside each LCOE exhibit, matching citations everywhere else in
 * the document without repeating a multi-line source list beneath tables.
 *
 * P-GROUP `row` atom (William 2026-07-04) — the caption `<p>` is stamped
 * `data-layout-group="row"` so the paged deck's break-inside:avoid rule moves
 * it WHOLE to one page. Without it, a caption straddling a page boundary was
 * block-cloned onto BOTH the page foot AND the next-page head (the duplicated
 * "Sources: … Last accurate …" defect William saw in the LCOE section).
 * Stamping the SHARED component protects ALL THREE render sites at once
 * (LcoeChartSection · LcoeBreakdownSection · the inline feasibility LCOE block)
 * per the fix-propagation rule — not just the one call site that reported it.
 * `<p>` is already a block, so break-inside applies (it is inert on inline).
 */
export const LcoeProvenanceFootnote: React.FC<{ report: SiteRagReport; className?: string }> = ({
  report,
  className = 'mt-1',
}) => {
  const { asOfLine } = resolveLcoeDataProvenance(report);
  const projection = projectDerivedTableReferences(report, { forceLcoe: true });
  const markerText = projection.lcoeReferenceNumbers
    .map((referenceNumber) => `[Ref ${referenceNumber}]`)
    .join(' ');
  return (
    <ReportSourceNote
      className={className}
      keepTogether
      testId="lcoe-provenance-footnote"
    >
      {markerText ? (
        <>
          References: {walkCitationText(markerText, projection.report.references)} ·{' '}
        </>
      ) : null}
      Full source details are listed in Appendix &amp; References. {asOfLine}
    </ReportSourceNote>
  );
};
